import AppButton from "@common/components/app-button";
import {ReactComponent as PreviewIcon} from "@assets/icons/preview.svg";
import {ReactComponent as PencilIcon} from "@assets/icons/pencil.svg";
import {ReactComponent as DeleteIcon} from "@assets/icons/delete.svg";
import {ReactComponent as PrintIcon} from "@assets/icons/print.svg";
import {ReactComponent as ShareIcon} from "@assets/icons/share.svg";
import {ReactComponent as QrCodeIcon} from "@assets/icons/qr-code.svg";
import {ReactComponent as EyeSlashIcon} from "@assets/icons/eye-slash.svg";
import {ReactComponent as EyeIcon} from "@assets/icons/eye-1.svg";
import {useLocation, useNavigate} from "react-router-dom";
import {disablePropagationHandler} from "@utils/disable-propagation-handler";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import {useLoading} from "../../core/hooks/use-loading.hook";
import {classNames} from "@utils/class-names";
import {DeleteArticleModalData} from "./delete-article-modal";
import {useArticleApi} from "../../core/hooks/use-article-api";
import {StarIcon} from "@heroicons/react/24/outline";
import {PreviewArticleModalData} from "./preview-article-modal";
import {ArticleToPrintModelData} from "./article-to-print-modal";
import {ShareArticleModalData} from "./share-article-modal";
import SpinnerLoader from "@common/components/spinner-loader";
import React from "react";
import {useActionTrigger} from "../../core/hooks/use-action.trigger";
import ArticlesListView from "../views/articles-list-view";
import {ArticleQrInfoModalData} from "./article-qr-info-modal";
import {convertLexicalJsonToPlainText} from "../helpers/lexical-to-plain-text";
import sanitizeUrlString from "@utils/sanitize-url-string";
import {PublishArticleModalData} from "./publish-article-modal";

interface ArticleActionsProps {
    showEditAction?: boolean;
    showToggleVisibilityAction?: boolean;
    showSetUniversalAction?: boolean;
    showSetPostoperativeUniversalAction?: boolean;
    canDelete?: boolean;
    articleId?: string;
    isVisibleArticle?: boolean;
    isUniversalArticle?: boolean;
    onClosePopover?: () => void;
    getArticleData?: () => ArticleDetails | Promise<ArticleDetails>;
}

const ArticleActions = (props: ArticleActionsProps) => {
    const {
        isVisibleArticle = false,
        isUniversalArticle = false,
        showEditAction = false,
        showToggleVisibilityAction = false,
        showSetUniversalAction = false,
        showSetPostoperativeUniversalAction = false,
        articleId,
        canDelete = false,
        onClosePopover,
        getArticleData
    } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const {showModal: showPreviewModal} = useModalWithData<PreviewArticleModalData>(ModalIds.PREVIEW_ARTICLE);
    const {showModal: showPrintModal} = useModalWithData<ArticleToPrintModelData>(ModalIds.PRINT_ARTICLE);
    const {showModal: showDeleteModal} = useModalWithData<DeleteArticleModalData>(ModalIds.ARTICLE_DELETE_CONFIRM);
    const {showModal: showQrModal} = useModalWithData<ArticleQrInfoModalData>(ModalIds.ARTICLE_QR_INFO);
    const {showModal: showShareModal} = useModalWithData<ShareArticleModalData>(ModalIds.SHARE_ARTICLE);
    const {showModal: showUniversalDeleteInfoModal} = useModalWithData<void>(ModalIds.ARTICLE_DELETE_BLOCKED_UNIVERSAL);
    const {showModal: showPublishModal} = useModalWithData<PublishArticleModalData>(ModalIds.ARTICLE_PUBLISH_CONFIRM);

    const {
        setUniversalArticle,
        setPostoperativeUniversalArticle,
        hideArticle,
        publishArticleFromList
    } = useArticleApi();
    const clickEditArticle = () => navigate(`./edit/${articleId}`)
    const {isLoading, startLoading, stopLoading} = useLoading();
    const triggerAction = useActionTrigger();
    const isCreatingMode = location.pathname.includes("/new");

    const clickPreviewArticleHandler = async (): Promise<void> => {
        startLoading("startPreviewArticleLoading");
        const data: ArticleDetails | undefined = getArticleData && await getArticleData();
        if (onClosePopover) onClosePopover();
        showPreviewModal({articleData: data});
        stopLoading("startPreviewArticleLoading");
    }

    const clickPrintArticleHandler = async (): Promise<void> => {
        startLoading(["startPrintArticleLoading", "articleToPrintPrepare"]);
        const data: ArticleDetails | undefined = getArticleData && await getArticleData();
        showPrintModal({
            articleData: data,
            onPrint: () => onClosePopover && onClosePopover()
        });
        stopLoading("startPrintArticleLoading");
    }

    const clickShareArticleHandler = async (): Promise<void> => {
        const data: ArticleDetails | undefined = getArticleData && await getArticleData();
        if (onClosePopover) onClosePopover();

        const plainTextTitle = convertLexicalJsonToPlainText(data?.title || "");
        const shareUrl = `${window.location.origin}/article/${data!.appId}/${sanitizeUrlString(plainTextTitle)}`;
        showShareModal({
            articleId: data?.id ?? articleId,
            articleTitle: data?.title,
            shareUrl,
        });
    }

    const clickDeleteArticleHandler = async (): Promise<void> => {
        if (!canDelete) {
            if (onClosePopover) onClosePopover();
            showUniversalDeleteInfoModal();
            return;
        }
        const data: ArticleDetails | undefined = getArticleData && await getArticleData();
        if (onClosePopover) onClosePopover();
        showDeleteModal({
            articleId: data?.id ?? articleId,
            articleTitle: data?.title,
            onSuccess: () => triggerAction(ArticlesListView),
        });
    }

    const clickQrCodeArticleHandler = async (): Promise<void> => {
        const data: ArticleDetails | undefined = getArticleData && await getArticleData();
        if (onClosePopover) onClosePopover();
        const plainTextTitle = convertLexicalJsonToPlainText(data?.title || "");
        const shareUrl = `${window.location.origin}/article/${data!.appId}/${sanitizeUrlString(plainTextTitle)}`;
        showQrModal({
            title: data?.title || "",
            url: shareUrl,
        });
    }

    const clickSetUniversalHandler = async (): Promise<void> => {
        if (!articleId) return;
        try {
            await setUniversalArticle(articleId);
            triggerAction(ArticlesListView);
            if (onClosePopover) onClosePopover();
        } catch (error) {
            console.error('Błąd podczas ustawiania uniwersalnego artykułu:', error);
        }
    }

    const clickSetPostoperativeUniversalHandler = async (): Promise<void> => {
        if (!articleId) return;
        try {
            await setPostoperativeUniversalArticle(articleId);
            triggerAction(ArticlesListView);
            if (onClosePopover) onClosePopover();
        } catch (error) {
            console.error('Błąd podczas ustawiania uniwersalnego pozabiegowego artykułu:', error);
        }
    }

    const clickHideArticleHandler = async (): Promise<void> => {
        try {
            const data = getArticleData && await getArticleData();
            if (!data) return;
            await hideArticle(data);
            triggerAction(ArticlesListView);
            if (onClosePopover) onClosePopover();
        } catch (error) {
            console.error('Błąd podczas ukrywania artykułu:', error);
        }
    };

    const clickPublishArticleHandler = async (): Promise<void> => {
        const data = getArticleData && await getArticleData();
        if (!data) return;
        if (onClosePopover) onClosePopover();
        showPublishModal({
            articleId: data.id,
            onConfirm: async () => {
                await publishArticleFromList(data);
                triggerAction(ArticlesListView);
            },
            onViewInEditor: () => navigate(`./edit/${data.id}`),
        });
    };

    return (
        <div
            onClick={disablePropagationHandler}
            className="flex flex-col bg-surface-level-1 py-2 rounded-2xl border border-outline-mild shadow-lvl-2 overflow-hidden">
            <AppButton
                onClick={clickPreviewArticleHandler}
                loaderColor="#333"
                loading={isLoading("startPreviewArticleLoading")}
                className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                classNameContentWrapper={classNames(
                    "!flex !flex-row-reverse !gap-1 [&>div]:w-full",
                    isLoading("startPreviewArticleLoading") && "!pr-4"
                )}>
                <div className={classNames(
                    "flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap",
                    isLoading("startPreviewArticleLoading") && "!pr-2"
                )}>
                    <PreviewIcon/>
                    <span>Zobacz podgląd</span>
                </div>
            </AppButton>
            {showEditAction && (
                <AppButton
                    onClick={clickEditArticle}
                    className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                    classNameContentWrapper="!block w-full">
                    <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                        <PencilIcon/>
                        <span>Edytuj</span>
                    </div>
                </AppButton>
            )}
            {showSetUniversalAction && articleId && (
                <AppButton
                    onClick={clickSetUniversalHandler}
                    loaderColor="#333"
                    loading={isLoading("setUniversalArticle")}
                    className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                    classNameContentWrapper="!block w-full">
                    <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                        <StarIcon className="w-5 h-5"/>
                        <span>Ustaw jako uniwersalny</span>
                    </div>
                </AppButton>
            )}
            {showSetPostoperativeUniversalAction && articleId && (
                <AppButton
                    onClick={clickSetPostoperativeUniversalHandler}
                    loaderColor="#333"
                    loading={isLoading("setUniversalArticle")}
                    className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                    classNameContentWrapper="!block w-full">
                    <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                        <StarIcon className="w-5 h-5"/>
                        <span>Ustaw jako uniwersalny pozabiegowy</span>
                    </div>
                </AppButton>
            )}
            {showToggleVisibilityAction && !isUniversalArticle && articleId && (
                isVisibleArticle ? (
                    <AppButton
                        onClick={clickHideArticleHandler}
                        loaderColor="#333"
                        loading={isLoading("hideArticle")}
                        className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                        classNameContentWrapper="!block w-full">
                        <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                            <EyeSlashIcon className="w-5 h-5"/>
                            <span>Ukryj artykuł</span>
                        </div>
                    </AppButton>
                ) : (
                    <AppButton
                        onClick={clickPublishArticleHandler}
                        loaderColor="#333"
                        loading={isLoading("publishArticleFromList")}
                        className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                        classNameContentWrapper="!block w-full">
                        <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                            <EyeIcon className="w-5 h-5"/>
                            <span>Ustaw jako widoczny</span>
                        </div>
                    </AppButton>
                )
            )}
            <AppButton
                onClick={clickPrintArticleHandler}
                loading={false}
                className={classNames(
                    "!p-0 !border-0 hover:bg-on-surface/5 !rounded-none",
                    isLoading("articleToPrintPrepare") && "!bg-on-surface/5"
                )}
                classNameContentWrapper="!flex !flex-row-reverse !gap-1 [&>div]:w-full">
                <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap items-center justify-between">
                    <div className="flex gap-4 items-center">
                        <PrintIcon/>
                        <span>Drukuj</span>
                    </div>
                    <div className="relative left-3">
                        {isLoading(["startPrintArticleLoading", "articleToPrintPrepare"]) &&
                            <SpinnerLoader color="#333"/>}
                    </div>
                </div>
            </AppButton>
            {!isCreatingMode && (
                <AppButton
                    onClick={clickShareArticleHandler}
                    className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                    classNameContentWrapper="!block w-full">
                    <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                        <ShareIcon/>
                        <span>Udostępnij</span>
                    </div>
                </AppButton>
            )}
            {!isCreatingMode && (
                <AppButton
                    onClick={clickQrCodeArticleHandler}
                    className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                    classNameContentWrapper="!block w-full">
                    <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                        <QrCodeIcon/>
                        <span>Generuj ulotkę QR</span>
                    </div>
                </AppButton>
            )}
            <AppButton
                onClick={clickDeleteArticleHandler}
                className="!p-0 !border-0 hover:bg-on-surface/5 !rounded-none"
                classNameContentWrapper="!block w-full">
                <div className="flex gap-4 py-2 pl-4 pr-6 whitespace-nowrap">
                    <DeleteIcon/>
                    <span>Usuń</span>
                </div>
            </AppButton>
        </div>
    )
}

export default ArticleActions
